import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { Card, Button, Table } from "react-bootstrap";
import {
    ArrowLeft,
    ChevronDown,
    ChevronUp,
    ArrowUpCircle,
    ArrowDownCircle,
    ArrowLeftRight,
} from "react-bootstrap-icons";
import Header from "../../../layouts/Header";
import Footer from "../../../layouts/Footer";
import { useNavigate } from "react-router-dom";
import AOVInsightDrawer from "./AOVInsightDrawer";

const AOVInsightDetails = () => {
    const navigate = useNavigate();
    const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
    const [skin, setSkin] = useState(currentSkin);
    const accessToken = localStorage.getItem('authToken');

    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [hoveredBar, setHoveredBar] = useState(null);
    const [showMore, setShowMore] = useState(false);

    const [groupBy, setGroupBy] = useState("Daily");
    const [dateRange, setDateRange] = useState("Last 30 Days");
    const [showGroupMenu, setShowGroupMenu] = useState(false);
    const [showRangeMenu, setShowRangeMenu] = useState(false);
    const [selectedDay, setSelectedDay] = useState("");
    const [outlets, setOutlets] = useState([]);
    const [selectedOutletId, setSelectedOutletId] = useState("all");
    const [showOutletMenu, setShowOutletMenu] = useState(false);
    const [showOutletCompareDrawer, setShowOutletCompareDrawer] = useState(false);
    const filterRef = useRef(null);

    const groupOptions = ["Daily", "Day based", "Weekly", "Monthly", "Quarterly", "Yearly"];
    // const rangeOptions = ["Today", "Yesterday", "Last 7 Days", "Last 30 Days", "Last 90 Days", "Last 3 Months", "Last 6 Months", "Last 12 Months"];
    const rangeOptions = ["Today", "Yesterday", "Last 7 Days", "Last 30 Days", "Last 90 Days"];
    const dayOptions = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun", "Weekend", "Weekday"];

    // Change this endpoint if your route is different.
    const AOV_INSIGHT_API_URL = "/api/aov-insight";

    // Pass selected outlet id here if you have outlet dropdown/context.
    // Example: const selectedOutletId = localStorage.getItem("selectedOutletId") || "all";

    const groupByMap = {
        Daily: "daily",
        "Day based": "dayBased",
        Weekly: "weekly",
        Monthly: "monthly",
        Quarterly: "quarterly",
        Yearly: "yearly",
    };

    const dateRangeMap = {
        "Today": "today",
        "Yesterday": "yesterday",
        "Last 7 Days": "last7days",
        "Last 30 Days": "last30days",
        "Last 90 Days": "last90days",
        "Last 3 Months": "last3months",
        "Last 6 Months": "last6months",
        "Last 12 Months": "last12months",
    };

    const [aovData, setAovData] = useState([]);
    const [orderValueBreakup, setOrderValueBreakup] = useState([]);
    const [summary, setSummary] = useState({
        totalOrders: 0,
        totalSales: 0,
        aov: 0,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const formatNumber = (value, decimals = 2) => {
        const num = Number(value || 0);
        return Number(num.toFixed(decimals));
    };

    const formatTimePeriodLabel = (value) => {
        if (!value) return "-";

        // daily / dayBased response: 2026-06-25
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            const [year, month, day] = value.split("-").map(Number);
            return new Intl.DateTimeFormat("en-IN", {
                day: "numeric",
                month: "short",
                year: "2-digit",
            })
                .format(new Date(year, month - 1, day))
                .replace(",", "");
        }

        // monthly response: 2026-06
        if (/^\d{4}-\d{2}$/.test(value)) {
            const [year, month] = value.split("-").map(Number);
            return new Intl.DateTimeFormat("en-IN", {
                month: "short",
                year: "numeric",
            }).format(new Date(year, month - 1, 1));
        }

        // weekly response from service: 2026-W26
        if (/^\d{4}-W\d+$/.test(value)) {
            return value.replace("-W", " Week ");
        }

        // quarterly response from service: 2026-Q2
        if (/^\d{4}-Q\d$/.test(value)) {
            const [year, quarter] = value.split("-Q");
            return `Q${quarter} ${year}`;
        }

        return value;
    };

    const normalizeCustomerAOV = (items = []) => {
        return items.map((item) => ({
            date: formatTimePeriodLabel(item.timePeriod || item.date),
            repeatAov: formatNumber(item.repeatAOV ?? item.repeatAov),
            newAov: formatNumber(item.newAOV ?? item.newAov),
            overallAov: formatNumber(item.overallAOV ?? item.overallAov),
        }));
    };

    const normalizeOrderRanges = (items = []) => {
        const totalOrders = items.reduce(
            (sum, item) => sum + Number(item.orders || 0),
            0
        );

        return items.map((item) => ({
            range: item.range || "-",
            orders: Number(item.orders || 0),
            contribution:
                item.contribution !== undefined
                    ? formatNumber(item.contribution)
                    : totalOrders
                        ? formatNumber((Number(item.orders || 0) / totalOrders) * 100)
                        : 0,
            aov: formatNumber(item.aov),
        }));
    };

    const getOutlets = async () => {
        setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get(`https://api.xpansecafe.com/outlet`, { headers });

            if (response?.data?.status === true) {
                const outletsData =
                    response?.data?.data?.outlets || [];

                const outletOptions = [
                    {
                        value: "all",
                        label: "All Outlets"
                    },
                    ...outletsData.map(outlet => ({
                        value: outlet._id,
                        label: outlet.name || outlet.outletName || "Unknown Outlet",
                        status: outlet.status
                    }))
                ];

                setOutlets(outletOptions);
            }
        } catch (error) {
            console.log(error, 'Failed to fetch outlets');
        } finally {
            setLoading(false);
        }
    };

    const fetchAOVInsight = async () => {
        try {
            setLoading(true);
            setError("");

            const params = {
                outletId: selectedOutletId || "all",
                filter: dateRangeMap[dateRange] || "last30days",
                groupBy: groupByMap[groupBy] || "daily",
            };

            if (groupBy === "Day based" && selectedDay) {
                params.selectedDay = selectedDay;
            }

            const response = await axios.get(`http://13.202.138.36:3000/smart-insights/fetch-aov`, { params });
            const payload = response?.data?.data || response?.data || {};

            setSummary(
                payload.summary || {
                    totalOrders: 0,
                    totalSales: 0,
                    aov: 0,
                }
            );

            setAovData(normalizeCustomerAOV(payload.customerAOV || payload.trend || []));
            setOrderValueBreakup(normalizeOrderRanges(payload.orderValueRanges || []));
        } catch (err) {
            console.error("AOV insight fetch error:", err);
            setError(
                err?.response?.data?.message ||
                err?.message ||
                "Unable to fetch AOV insight."
            );
            setAovData([]);
            setOrderValueBreakup([]);
            setSummary({
                totalOrders: 0,
                totalSales: 0,
                aov: 0,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (groupBy === "Day based" && !selectedDay) return;
        fetchAOVInsight();
        getOutlets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupBy, dateRange, selectedDay, selectedOutletId]);

    useEffect(() => {
        if (groupBy !== "Day based") {
            setSelectedDay("");
        }
    }, [groupBy]);

    const filteredAovData = aovData;
    const current = filteredAovData[filteredAovData.length - 1] || {
        date: "-",
        repeatAov: 0,
        newAov: 0,
        overallAov: 0,
    };

    const previous = filteredAovData[filteredAovData.length - 2] || current;

    const change = current.overallAov - previous.overallAov;
    const changePercent = previous.overallAov ? (change / previous.overallAov) * 100 : 0;
    const isPositive = change >= 0;

    const maxOrders = Math.max(...orderValueBreakup.map((item) => item.orders), 1);
    const displayedBreakup = showMore ? orderValueBreakup : orderValueBreakup.slice(0, 5);

    const periodLabel =
        groupBy === "Day based" && selectedDay
            ? `${selectedDay} in ${dateRange.toLowerCase()}`
            : dateRange.toLowerCase();

    const chartMeta = useMemo(() => {
        const values = filteredAovData.map((item) => Number(item.overallAov || 0));

        if (!values.length) {
            return {
                minValue: 0,
                maxValue: 100,
                yAxisLabels: [100, 80, 60, 40, 20, 0],
                xAxisLabels: [],
            };
        }

        const actualMin = Math.min(...values);
        const actualMax = Math.max(...values);
        const range = actualMax - actualMin;
        const padding = range === 0 ? actualMax * 0.1 || 10 : range * 0.15;

        const rawMin = actualMin - padding;
        const rawMax = actualMax + padding;

        const stepBase = Math.pow(10, Math.floor(Math.log10(rawMax - rawMin || 1)));
        const step = Math.ceil((rawMax - rawMin) / 5 / stepBase) * stepBase;

        const minValue = Math.floor(rawMin / step) * step;
        const maxValue = Math.ceil(rawMax / step) * step;

        const yAxisLabels = Array.from({ length: 6 }, (_, index) =>
            Math.round(maxValue - ((maxValue - minValue) / 5) * index)
        );

        const xLabelCount = Math.min(5, filteredAovData.length);

        const xAxisLabels = Array.from({ length: xLabelCount }, (_, index) => {
            const dataIndex =
                xLabelCount === 1
                    ? 0
                    : Math.round((index * (filteredAovData.length - 1)) / (xLabelCount - 1));

            return {
                label: filteredAovData[dataIndex].date,
                left:
                    filteredAovData.length === 1
                        ? 0
                        : (dataIndex / (filteredAovData.length - 1)) * 100,
            };
        });

        return { minValue, maxValue, yAxisLabels, xAxisLabels };
    }, [filteredAovData]);

    const points = useMemo(() => {
        return filteredAovData.map((item, index) => {
            const x =
                filteredAovData.length === 1
                    ? 0
                    : (index / (filteredAovData.length - 1)) * 100;

            const y =
                chartMeta.maxValue === chartMeta.minValue
                    ? 50
                    : 100 -
                    ((item.overallAov - chartMeta.minValue) /
                        (chartMeta.maxValue - chartMeta.minValue)) *
                    100;

            return { ...item, x, y };
        });
    }, [filteredAovData, chartMeta]);

    const linePath = points
        .map((point, index) =>
            index === 0 ? `M ${point.x} ${point.y}` : `L ${point.x} ${point.y}`
        )
        .join(" ");

    const areaPath = points.length ? `${linePath} L 100 100 L 0 100 Z` : "";

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (filterRef.current && !filterRef.current.contains(e.target)) {
                setShowOutletMenu(false);
                setShowGroupMenu(false);
                setShowRangeMenu(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <>
            <Header onSkin={setSkin} />

            <div className="main main-app">
                <div style={{ background: "#ffffff", minHeight: "100vh", padding: 28 }}>
                    <div style={topActionStyle}>
                        <Button
                            variant="light"
                            onClick={() => navigate("/dashboard")}
                            style={backButtonStyle}
                        >
                            <ArrowLeft size={18} /> Go Back
                        </Button>

                        <div ref={filterRef} style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                            <div style={dropdownWrapStyle}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowOutletMenu(!showOutletMenu);
                                        setShowGroupMenu(false);
                                        setShowRangeMenu(false);
                                    }}
                                    style={showOutletMenu ? activeFilterStyle : filterStyle}
                                >
                                    {
                                        outlets.find(o => o.value === selectedOutletId)?.label ||
                                        "All Outlets"
                                    }
                                    <ChevronDown size={18} />
                                </button>

                                {showOutletMenu && (
                                    <div style={menuStyle}>
                                        {outlets.map((outlet) => (
                                            <button
                                                key={outlet.value}
                                                type="button"
                                                onClick={() => {
                                                    setSelectedOutletId(outlet.value);
                                                    setShowOutletMenu(false);
                                                }}
                                                style={menuItemStyle}
                                            >
                                                {outlet.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div style={dropdownWrapStyle}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowGroupMenu(!showGroupMenu);
                                        setShowRangeMenu(false);
                                    }}
                                    style={showGroupMenu ? activeFilterStyle : filterStyle}
                                >
                                    {groupBy === "Day based" && selectedDay ? selectedDay : groupBy}
                                    <ChevronDown size={18} />
                                </button>

                                {showGroupMenu && (
                                    <div style={menuStyle}>
                                        {groupOptions.map((option) => (
                                            <button
                                                key={option}
                                                type="button"
                                                onClick={() => {
                                                    setGroupBy(option);

                                                    if (option !== "Day based") {
                                                        setSelectedDay("");
                                                        setShowGroupMenu(false);
                                                    }
                                                }}
                                                style={menuItemStyle}
                                            >
                                                {option}
                                            </button>
                                        ))}

                                        {groupBy === "Day based" && (
                                            <div style={dayPopupInnerStyle}>
                                                <div style={dayPopupTitleStyle}>Select Day</div>

                                                <div style={dayGridStyle}>
                                                    {dayOptions.map((day) => (
                                                        <button
                                                            key={day}
                                                            type="button"
                                                            onClick={() => {
                                                                setSelectedDay(day);
                                                                setShowGroupMenu(false);
                                                            }}
                                                            style={
                                                                selectedDay === day
                                                                    ? dayBtnActiveStyle
                                                                    : dayBtnStyle
                                                            }
                                                        >
                                                            {day}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div style={dropdownWrapStyle}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowRangeMenu(!showRangeMenu);
                                        setShowGroupMenu(false);
                                    }}
                                    style={showRangeMenu ? activeFilterStyle : filterStyle}
                                >
                                    {dateRange}
                                    <ChevronDown size={18} />
                                </button>

                                {showRangeMenu && (
                                    <div style={menuStyle}>
                                        {rangeOptions.map((option) => (
                                            <button
                                                key={option}
                                                type="button"
                                                onClick={() => {
                                                    setDateRange(option);
                                                    setShowRangeMenu(false);
                                                }}
                                                style={menuItemStyle}
                                            >
                                                {option}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <h1 style={pageTitleStyle}>
                        Is our <span style={{ color: "#8B4DFF" }}>average order value</span>{" "}
                        growing over time?
                    </h1>

                    {error && (
                        <div style={errorBoxStyle}>
                            {error}
                        </div>
                    )}

                    {loading && (
                        <div style={loadingBoxStyle}>
                            Loading AOV insight...
                        </div>
                    )}

                    <Card style={mainCardStyle}>
                        <Card.Body style={{ padding: 24 }}>
                            <div className="details-top-grid" style={topGridStyle}>
                                <div>
                                    {/* <h1 style={amountStyle}>₹{Math.round(current.overallAov || summary.aov || 0)}</h1> */}
                                    <h1 style={amountStyle}>₹{Math.round(summary.aov || 0)}</h1>
                                    <p style={subTextStyle}>AOV in {periodLabel}</p>

                                    <div style={chartOuterStyle}>
                                        <div style={yAxisStyle}>
                                            {chartMeta.yAxisLabels.map((label, index) => (
                                                <span key={`${label}-${index}`}>₹{label}</span>
                                            ))}
                                        </div>

                                        <div style={chartInnerStyle}>
                                            {chartMeta.yAxisLabels.map((label, index) => (
                                                <div key={`${label}-${index}`} style={gridLineStyle} />
                                            ))}

                                            {points.length > 0 && (
                                                <svg
                                                    viewBox="0 0 100 100"
                                                    preserveAspectRatio="none"
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        position: "absolute",
                                                        left: 0,
                                                        top: 0,
                                                        overflow: "visible",
                                                    }}
                                                >
                                                    <defs>
                                                        <linearGradient
                                                            id="aovGradient"
                                                            x1="0"
                                                            y1="0"
                                                            x2="0"
                                                            y2="1"
                                                        >
                                                            <stop offset="0%" stopColor="#A56CFF" stopOpacity="0.75" />
                                                            <stop offset="100%" stopColor="#F3EAFE" stopOpacity="0.55" />
                                                        </linearGradient>
                                                    </defs>

                                                    <path d={areaPath} fill="url(#aovGradient)" />

                                                    <path
                                                        d={linePath}
                                                        fill="none"
                                                        stroke="#8B4DFF"
                                                        strokeWidth="2.2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        vectorEffect="non-scaling-stroke"
                                                    />

                                                    {points.map((point, index) => (
                                                        <circle
                                                            key={index}
                                                            cx={point.x}
                                                            cy={point.y}
                                                            r={hoveredIndex === index ? 1.8 : 1.1}
                                                            fill="#ffffff"
                                                            stroke="#8B4DFF"
                                                            strokeWidth="1.4"
                                                            vectorEffect="non-scaling-stroke"
                                                            onMouseEnter={() => setHoveredIndex(index)}
                                                            onMouseLeave={() => setHoveredIndex(null)}
                                                        />
                                                    ))}
                                                </svg>
                                            )}
                                        </div>
                                    </div>

                                    <div style={xAxisStyle}>
                                        {chartMeta.xAxisLabels.map((item, index) => (
                                            <span
                                                key={`${item.label}-${index}`}
                                                style={{
                                                    position: "absolute",
                                                    left: `${item.left}%`,
                                                    transform:
                                                        index === 0
                                                            ? "translateX(0)"
                                                            : index === chartMeta.xAxisLabels.length - 1
                                                                ? "translateX(-100%)"
                                                                : "translateX(-50%)",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {item.label}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div
                                    style={{
                                        background: isPositive
                                            ? "linear-gradient(135deg, #E8FFF4 0%, #ffffff 100%)"
                                            : "linear-gradient(135deg, #FFEAEA 0%, #ffffff 100%)",
                                        border: isPositive ? "1px solid #B7F7D8" : "1px solid #FECACA",
                                        borderRadius: 18,
                                        padding: 20,
                                    }}
                                >
                                    <h3 style={insightTitleStyle}>
                                        {isPositive ? "👏 Going well" : "⚠ Needs attention"}
                                    </h3>

                                    <p style={insightTextStyle}>
                                        AOV {isPositive ? "increased" : "decreased"} by ₹
                                        {Math.abs(change).toFixed(2)}
                                    </p>

                                    <span
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 6,
                                            background: "#ffffff",
                                            color: isPositive ? "#07996F" : "#EF4444",
                                            padding: "7px 12px",
                                            borderRadius: 20,
                                            fontWeight: 700,
                                            fontSize: 13,
                                            marginBottom: 22,
                                        }}
                                    >
                                        {isPositive ? <ArrowUpCircle size={16} /> : <ArrowDownCircle size={16} />}
                                        {Math.abs(changePercent).toFixed(2)}% vs previous period
                                    </span>

                                    <CompareLine
                                        color="#8B4DFF"
                                        label={current.date}
                                        value={`₹${Number(current.overallAov || 0).toFixed(3)}`}
                                        amount={current.overallAov}
                                        maxAmount={chartMeta.maxValue}
                                    />

                                    <CompareLine
                                        color="#344244"
                                        label={previous.date}
                                        value={`₹${Number(previous.overallAov || 0).toFixed(3)}`}
                                        amount={previous.overallAov}
                                        maxAmount={chartMeta.maxValue}
                                    />

                                    <div style={summaryBoxStyle}>
                                        <div>
                                            <span style={summaryLabelStyle}>Repeat AOV</span>
                                            <strong style={summaryValueStyle}>₹{current.repeatAov}</strong>
                                        </div>

                                        <div>
                                            <span style={summaryLabelStyle}>New AOV</span>
                                            <strong style={summaryValueStyle}>₹{current.newAov}</strong>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    <h2 style={sectionTitleStyle}>
                        Which order value range is{" "}
                        <span style={{ color: "#6F3DF4" }}>driving AOV?</span>
                    </h2>

                    <Card style={mainCardStyle}>
                        <Card.Body style={{ padding: 22 }}>
                            {displayedBreakup.length === 0 && (
                                <div style={emptyBoxStyle}>
                                    No order value breakup found for selected filters.
                                </div>
                            )}

                            {displayedBreakup.map((item, index) => {
                                const widthPercent = (item.orders / maxOrders) * 100;
                                const width = `${widthPercent}%`;
                                const labelCanFitInside = widthPercent >= 22;

                                const barColor =
                                    index === 0
                                        ? "#4B1FC9"
                                        : index === 1
                                            ? "#6F3DF4"
                                            : index === 2
                                                ? "#8B4DFF"
                                                : "#CFA8FF";

                                return (
                                    <div
                                        key={item.range}
                                        className="aov-row"
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "170px 1fr",
                                            alignItems: "center",
                                            gap: 18,
                                            marginBottom: 22,
                                        }}
                                    >
                                        <div style={rangeLabelStyle}>{item.range}</div>

                                        <div
                                            style={barTrackStyle}
                                            onMouseEnter={() => setHoveredBar(index)}
                                            onMouseLeave={() => setHoveredBar(null)}
                                        >
                                            {hoveredBar === index && (
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        bottom: 56,
                                                        left: `${Math.min(Math.max(widthPercent, 18), 82)}%`,
                                                        transform: "translateX(-50%)",
                                                        width: 250,
                                                        background: "#ffffff",
                                                        borderRadius: 16,
                                                        padding: 12,
                                                        boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
                                                        zIndex: 100,
                                                        textAlign: "left",
                                                    }}
                                                >
                                                    <div style={tooltipTitleStyle}>{item.range}</div>

                                                    <TooltipRow label="Orders" value={item.orders} />
                                                    <TooltipRow label="Contribution" value={`${item.contribution}%`} />
                                                    <TooltipRow
                                                        label="Avg Value"
                                                        value={`₹${item.aov.toFixed(2)}`}
                                                        highlight
                                                    />

                                                    <div
                                                        style={{
                                                            position: "absolute",
                                                            bottom: -8,
                                                            left: "50%",
                                                            transform: "translateX(-50%) rotate(45deg)",
                                                            width: 16,
                                                            height: 16,
                                                            background: "#ffffff",
                                                        }}
                                                    />
                                                </div>
                                            )}

                                            <div
                                                style={{
                                                    width,
                                                    height: "100%",
                                                    background: barColor,
                                                    borderRadius: 8,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: labelCanFitInside ? "flex-end" : "center",
                                                    padding: labelCanFitInside ? "0 14px" : "0 6px",
                                                    transition: "0.3s",
                                                    cursor: "pointer",
                                                    minWidth: labelCanFitInside ? 0 : 52,
                                                    maxWidth: "100%",
                                                    overflow: "hidden",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        ...insideBarTextStyle,
                                                        fontSize: labelCanFitInside ? 15 : 12,
                                                    }}
                                                >
                                                    {labelCanFitInside ? `${item.orders} orders` : item.orders}
                                                </span>
                                            </div>

                                            {!labelCanFitInside && (
                                                <span
                                                    style={{
                                                        position: "absolute",
                                                        right: 14,
                                                        top: "50%",
                                                        transform: "translateY(-50%)",
                                                        fontWeight: 700,
                                                        fontSize: 14,
                                                        color: "#1E293B",
                                                        whiteSpace: "nowrap",
                                                        pointerEvents: "none",
                                                    }}
                                                >
                                                    {item.orders} orders
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}

                            <button
                                type="button"
                                onClick={() => setShowMore(!showMore)}
                                style={showMoreButtonStyle}
                            >
                                {showMore ? "Show less" : "Show more"}
                                {showMore ? <ChevronUp /> : <ChevronDown />}
                            </button>
                        </Card.Body>
                    </Card>

                    <div style={{ marginTop: 36 }}>
                        <Table borderless responsive style={{ marginBottom: 0 }}>
                            <thead>
                                <tr>
                                    <th style={tableHeadStyle}>Time Period</th>
                                    <th style={tableHeadStyle}>AOV for Repeat Customers ↕</th>
                                    <th style={tableHeadStyle}>AOV for New Customers ↕</th>
                                    <th style={tableHeadStyle}>Overall AOV ↕</th>
                                </tr>
                            </thead>

                            <tbody>
                                {filteredAovData.length === 0 ? (
                                    <tr>
                                        <td style={tableCellStyle} colSpan={4}>
                                            No AOV data found for selected filters.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredAovData.map((item) => (
                                        <tr key={item.date}>
                                            <td style={tableCellStyle}>{item.date}</td>
                                            <td style={tableCellStyle}>₹{item.repeatAov}</td>
                                            <td style={tableCellStyle}>₹{item.newAov}</td>
                                            <td style={tableCellStyle}>₹{item.overallAov}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>
                </div>
            </div>

            <div style={floatingCompareContainerStyle}>
                <button
                    type="button"
                    onClick={() => setShowOutletCompareDrawer(true)}
                    style={compareOutletButtonStyle}
                >
                    <ArrowLeftRight size={12} />
                    Compare across Outlets
                </button>
            </div>

            <AOVInsightDrawer
                open={showOutletCompareDrawer}
                onClose={() => setShowOutletCompareDrawer(false)}
                dateRange={dateRange}
                groupBy={groupBy}
                selectedDay={selectedDay}
                accessToken={accessToken}
            />

            <div className="mt-4">
                <Footer />
            </div>

            <style>
                {`
                    tbody tr {
                        background: #f7fbfb;
                        border-radius: 14px;
                    }

                    tbody td {
                        padding: 18px 22px !important;
                        font-weight: 600;
                        color: #344244;
                    }

                    tbody tr td:first-child {
                        border-radius: 14px 0 0 14px;
                    }

                    tbody tr td:last-child {
                        border-radius: 0 14px 14px 0;
                    }

                    tbody tr {
                        border-bottom: 14px solid #fff;
                    }

                    @media (max-width: 992px) {
                        .details-top-grid {
                            grid-template-columns: 1fr !important;
                        }
                    }

                    @media (max-width: 768px) {
                        .aov-row {
                            grid-template-columns: 1fr !important;
                            gap: 10px !important;
                        }
                    }
                `}
            </style>
        </>
    );
};

const TooltipRow = ({ label, value, highlight }) => {
    return (
        <div
            style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 14,
                fontSize: 14,
                marginTop: 8,
            }}
        >
            <span style={{ color: "#64748B" }}>{label}</span>
            <strong style={{ color: highlight ? "#8B4DFF" : "#111827" }}>{value}</strong>
        </div>
    );
};

const CompareLine = ({ color, label, value, amount, maxAmount }) => {
    const lineWidth = Math.max((Number(amount || 0) / Math.max(Number(maxAmount || 0), 1)) * 180, 40);

    return (
        <div style={{ marginBottom: 14 }}>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 16,
                    marginBottom: 5,
                }}
            >
                <div
                    style={{
                        width: lineWidth,
                        height: 12,
                        borderRadius: 10,
                        background: color,
                    }}
                />

                <strong>{value}</strong>
            </div>

            <div style={{ color: "#5f7073", fontSize: 12 }}>{label}</div>
        </div>
    );
};

const dropdownWrapStyle = {
    position: "relative",
};

const barTrackStyle = {
    position: "relative",
    height: 44,
    display: "flex",
    alignItems: "center",
    background: "#F4F0FF",
    borderRadius: 10,
    overflow: "visible",
};

const pageTitleStyle = {
    fontSize: 32,
    fontWeight: 600,
    color: "#071C1F",
    marginBottom: 12,
};

const topActionStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
};

const backButtonStyle = {
    border: "1px solid #d8dee4",
    borderRadius: 14,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    gap: 8,
};

const filterStyle = {
    border: "1px solid #d8dee4",
    borderRadius: 10,
    fontWeight: 600,
    color: "#17292b",
    padding: "11px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 40,
    background: "#fff",
    minWidth: 150,
};

const activeFilterStyle = {
    ...filterStyle,
    borderColor: "#8B4DFF",
    boxShadow: "0 0 0 1px #8B4DFF",
};

const menuStyle = {
    position: "absolute",
    top: "calc(100% + 8px)",
    left: 0,
    background: "#fff",
    border: "1px solid #d8dee4",
    borderRadius: 10,
    boxShadow: "0 12px 30px rgba(0,0,0,0.12)",
    minWidth: 180,
    padding: "8px 0",
    zIndex: 50,
};

const menuItemStyle = {
    width: "100%",
    border: 0,
    background: "#fff",
    textAlign: "left",
    padding: "10px 16px",
    fontWeight: 500,
    color: "#17292b",
};

const dayPopupInnerStyle = {
    borderTop: "1px solid #edf1f2",
    marginTop: 6,
    padding: "12px 14px 8px",
    width: 300,
};

const dayPopupTitleStyle = {
    fontSize: 13,
    fontWeight: 700,
    color: "#687779",
    marginBottom: 12,
};

const dayGridStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
};

const dayBtnStyle = {
    border: "1px solid #cfd8da",
    background: "#fff",
    color: "#17292b",
    borderRadius: 8,
    padding: "8px 14px",
    fontWeight: 700,
};

const dayBtnActiveStyle = {
    ...dayBtnStyle,
    borderColor: "#8B4DFF",
    color: "#8B4DFF",
};

const mainCardStyle = {
    border: "1px solid #dce5e7",
    borderRadius: 18,
    boxShadow: "none",
};

const topGridStyle = {
    display: "grid",
    gridTemplateColumns: "1fr 360px",
    gap: 36,
    alignItems: "center",
};

const amountStyle = {
    fontSize: 48,
    fontWeight: 600,
    color: "#071C1F",
    marginBottom: 18,
};

const subTextStyle = {
    fontSize: 18,
    fontWeight: 500,
    color: "#071C1F",
    marginBottom: 26,
};

const chartOuterStyle = {
    display: "grid",
    gridTemplateColumns: "72px 1fr",
    alignItems: "stretch",
    height: 250,
};

const yAxisStyle = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    fontSize: 12,
    color: "#7b8794",
    padding: "0 10px 0 0",
    textAlign: "right",
};

const chartInnerStyle = {
    position: "relative",
    height: 250,
};

const gridLineStyle = {
    height: `${100 / 5}%`,
    borderTop: "1px solid #eef1f4",
};

const xAxisStyle = {
    marginLeft: 72,
    position: "relative",
    height: 20,
    color: "#7b8794",
    fontSize: 12,
    marginTop: 12,
};

const insightTitleStyle = {
    fontSize: 18,
    fontWeight: 800,
    marginBottom: 12,
    color: "#071C1F",
};

const insightTextStyle = {
    fontWeight: 700,
    marginBottom: 14,
    color: "#17292b",
};

const summaryBoxStyle = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    marginTop: 18,
};

const summaryLabelStyle = {
    display: "block",
    color: "#64748B",
    fontSize: 12,
    marginBottom: 4,
};

const summaryValueStyle = {
    display: "block",
    color: "#071C1F",
    fontSize: 16,
};

const sectionTitleStyle = {
    fontSize: 34,
    fontWeight: 600,
    color: "#071C1F",
    margin: "34px 0 22px",
};

const rangeLabelStyle = {
    fontSize: 14,
    fontWeight: 700,
    color: "#17292b",
};

const tooltipTitleStyle = {
    fontSize: 12,
    fontWeight: 800,
    color: "#071C1F",
    marginBottom: 10,
};

const insideBarTextStyle = {
    color: "#ffffff",
    fontWeight: 700,
    whiteSpace: "nowrap",
};

const showMoreButtonStyle = {
    border: 0,
    background: "transparent",
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 8,
};

const loadingBoxStyle = {
    background: "#F8F5FF",
    border: "1px solid #E6D8FF",
    color: "#6F3DF4",
    borderRadius: 12,
    padding: "12px 16px",
    marginBottom: 16,
    fontWeight: 700,
};

const errorBoxStyle = {
    background: "#FFF1F2",
    border: "1px solid #FECDD3",
    color: "#E11D48",
    borderRadius: 12,
    padding: "12px 16px",
    marginBottom: 16,
    fontWeight: 700,
};

const emptyBoxStyle = {
    background: "#F8FAFC",
    border: "1px dashed #CBD5E1",
    color: "#64748B",
    borderRadius: 12,
    padding: 18,
    fontWeight: 700,
};

const tableHeadStyle = {
    fontSize: 17,
    color: "#344244",
    fontWeight: 800,
    padding: "0 22px 16px",
    whiteSpace: "nowrap",
};

const tableCellStyle = {
    fontSize: 16,
    verticalAlign: "middle",
    whiteSpace: "nowrap",
};

const floatingCompareContainerStyle = {
    position: "fixed",
    bottom: 24,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 1050,
};

const compareOutletButtonStyle = {
    border: 0,
    background: "#122322",
    color: "#ffffff",
    borderRadius: 999,
    padding: "12px 18px",
    fontWeight: 800,
    fontSize: 12,
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    boxShadow: "0 12px 35px rgba(0,0,0,0.25)",
    transition: "all 0.2s ease",
};

export default AOVInsightDetails;